import { useEffect, useRef, useState, ReactNode } from 'react';

interface ShimmerOnScrollProps {
  children: ReactNode;
  className?: string;
}

export function ShimmerOnScroll({ children, className = '' }: ShimmerOnScrollProps) {
  const [hasShimmered, setHasShimmered] = useState(false);
  const [isShimmering, setIsShimmering] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Add shimmer animation keyframes
    const style = document.createElement('style');
    style.textContent = `
      @keyframes shimmer {
        0% {
          left: -100%;
        }
        100% {
          left: 100%;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasShimmered) {
            setIsShimmering(true);
            setHasShimmered(true);
            setTimeout(() => setIsShimmering(false), 800);
          }
        });
      },
      {
        threshold: 0.1, // Trigger when 10% of the element is visible
      }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
    };
  }, [hasShimmered]);

  return (
    <div ref={elementRef} className={`relative overflow-hidden ${className}`}>
      <div className="relative z-10">
        {children}
      </div>
      {isShimmering && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(10, 116, 255, 0.6), transparent)',
            animation: 'shimmer 0.8s ease-in-out',
            pointerEvents: 'none',
            zIndex: 1
          }}
        />
      )}
    </div>
  );
}
